import { Component, computed, inject, ChangeDetectionStrategy } from '@angular/core';
import { UtilityService } from '@eforall/common';
import { GridContentWidget, PageTabsMainTabGridSize } from '@eforall/dashboard';
import { AccSessionSpecialistSurveyResponseFuncData } from '@interfaces';
import { DomainDataService } from '../../../../../../services';
import { AcceleratorAreaService } from '../../../accelerator-area.service';
import { AccDeliveryService } from '../../acc-delivery.service';
import { getSetup } from './grid/get-setup';

export interface TopicSpecialistRow {
	accTopicSpecialistId: number,
	accTopicId: number,
	personId: number,
	topicId: number,
	accSessionId: number | undefined,
	eventId: number | undefined,
	topicName: string,
	sessionStartUTC: number | undefined,
	specialistSurveyResults: number | undefined,
	specialistSurveyProfessionalismScore: number | undefined,
	specialistSurveyKnowledgeScore: number | undefined,
	specialistSurveyEffectivenessScore: number | undefined,
	updatedUTC: number,
}


@Component({
	selector: 'acc-topic-specialists-view-part',
	standalone: true,
	imports: [GridContentWidget],
	changeDetection: ChangeDetectionStrategy.Eager,
	templateUrl: './topic-specialists-view.part.html',
})
export class AccTopicSpecialistsViewPart {

	private readonly util = inject(UtilityService);
	private readonly pageService = inject(AccDeliveryService);
	private readonly areaService = inject(AcceleratorAreaService);
	private readonly domain = inject(DomainDataService);

	public setup = getSetup(this.util);

	public pageTabsGridSize = computed<PageTabsMainTabGridSize | undefined>(() => {
		if (this.pageService.layout()) return { layout: this.pageService.layout()!, viewSelector: true };
		return undefined;
	});

	public topicSpecialists = computed<TopicSpecialistRow[]>(() => {
		const data = this.areaService.data();
		if (!data) return [];

		const sessions = data.sessions;
		const sessionMap = this.util.array.toMap(sessions, session => session.accSessionId);
		const sessionTopics = data.sessionTopics;
		const sessionTopicMap = this.util.array.toMap(sessionTopics, st => st.accTopicId);
		const accTopics = data.accTopics;
		const accTopicMap = this.util.array.toMap(accTopics, accTopic => accTopic.accTopicId);

		const topicSpecialists = data.topicSpecialists;
		const events = data.events;
		const eventMap = this.util.array.toMap(events, event => event.eventId);


		const ddTopics = this.domain.data().topics;
		const ddTopicMap = this.util.array.toMap(ddTopics, topic => topic.topicId);

		const specialistResponses = data.specialistSurveyResponses;
		const topicResponses = data.topicSurveyResponses;

		const rows: TopicSpecialistRow[] = [];
		for (const topicSpecialist of topicSpecialists) {

			const topicId = accTopicMap[topicSpecialist.accTopicId]?.topicId;
			if(!topicId) continue;

			const topic = ddTopicMap[topicId];
			if(!topic) continue;


			const accSessionId = sessionTopicMap[topicSpecialist.accTopicId]?.accSessionId;
			const eventId = accSessionId ? sessionMap[accSessionId]?.eventId: undefined;
			const event = eventId ? eventMap[eventId] : undefined;
			const accSessionTopicSurveyResponseIds = topicResponses.filter(tr => tr.topicId == topic.topicId).map(tr => tr.accSessionTopicSurveyResponseId);
			const allSpecialistResponses: AccSessionSpecialistSurveyResponseFuncData[] = specialistResponses.filter(r => accSessionTopicSurveyResponseIds.includes(r.accSessionTopicSurveyResponseId) && r.personId == topicSpecialist.personId);


			const row: TopicSpecialistRow = {
				accTopicSpecialistId: topicSpecialist.accTopicSpecialistId,
				accTopicId: topicSpecialist.accTopicId,
				accSessionId,
				eventId: event?.eventId,
				personId: topicSpecialist.personId,
				sessionStartUTC: event?.startUTC,
				topicId: topic.topicId,
				topicName: topic.name,
				specialistSurveyResults: allSpecialistResponses.length || undefined,
				specialistSurveyEffectivenessScore: this.util.nps.calcScore(allSpecialistResponses.map(result => result.effectiveness)) || undefined,
				specialistSurveyKnowledgeScore: this.util.nps.calcScore(allSpecialistResponses.map(result => result.knowledge)) || undefined,
				specialistSurveyProfessionalismScore: this.util.nps.calcScore(allSpecialistResponses.map(result => result.professionalism)) || undefined,
				updatedUTC: data.asOfUTC,
			};

			rows.push(row);
		}

		return rows;
	});
}